import { api } from './api';

/**
 * A friend's referral link: `<portal>/book/location?ref=CODE`.
 *
 * The code is kept for 30 days in localStorage (so a visitor who comes back
 * later still books as referred) and in the booking state for the current
 * booking. It is only ever a hint: the server checks the code again, with
 * every eligibility rule, when the booking is created (createWorkOrder's
 * `referralCode`), and simply ignores one that does not apply.
 */
export const REFERRAL_PARAM = 'ref';
export const REFERRAL_STORAGE_KEY = 'referralRef';
export const REFERRAL_TTL_MS = 30 * 24 * 60 * 60 * 1000;
export const REFERRAL_LOOKUP_ENDPOINT = 'api/referral';

type Stored = { code: string; savedAt: number };

/** `abc123` -> `ABC123`; null for anything that cannot be a code. */
export function normalizeReferralCode(value?: string | null): string | null {
  const code = String(value ?? '').trim().toUpperCase();
  return /^[A-Z0-9]{4,32}$/.test(code) ? code : null;
}

export function saveReferral(code: string, now: number = Date.now()): void {
  try {
    window.localStorage.setItem(
      REFERRAL_STORAGE_KEY,
      JSON.stringify({ code, savedAt: now } as Stored),
    );
  } catch (e) {
    // Private mode / blocked storage: the booking state still has it.
  }
}

export function clearReferral(): void {
  try {
    window.localStorage.removeItem(REFERRAL_STORAGE_KEY);
  } catch (e) {
    // best effort
  }
}

/** The stored code while it is younger than 30 days, else null. */
export function readReferral(now: number = Date.now()): string | null {
  try {
    const raw = window.localStorage.getItem(REFERRAL_STORAGE_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as Partial<Stored>;
    const code = normalizeReferralCode(stored?.code);
    const savedAt = Number(stored?.savedAt);
    if (!code || !Number.isFinite(savedAt) || now - savedAt > REFERRAL_TTL_MS) {
      clearReferral();
      return null;
    }
    return code;
  } catch (e) {
    return null;
  }
}

export type ReferralLookup = { valid: boolean; tenant: string | null };

/**
 * GET /api/referral/:code - `{ valid, tenant }` and nothing else. A network
 * problem answers `null` ("could not tell"), so the caller can keep the code
 * and let the server decide at booking time.
 */
export async function lookupReferralCode(
  code: string,
): Promise<ReferralLookup | null> {
  try {
    const { data } = await api.get(
      `${REFERRAL_LOOKUP_ENDPOINT}/${encodeURIComponent(code)}`,
    );
    return {
      valid: data?.valid === true,
      tenant: typeof data?.tenant === 'string' ? data.tenant : null,
    };
  } catch (e) {
    return null;
  }
}
